// Presentation-only extraction. Values remain patient-reported, not drug
// identity matches, clinical adjudications or medication instructions.
export const chartFormattingFields = ["name", "dose", "frequency", "reason", "lastTaken", "relationship", "phone"] as const;
export type ChartFormattingEntry = { sourceIndex: number } & Record<(typeof chartFormattingFields)[number], string | null>;
export interface QuestionnaireChartFormatting {
  version: 1;
  kind: "medications" | "contact";
  sourceValue: string | string[];
  entries: ChartFormattingEntry[];
  verified: true;
  lastDoseSourceValue?: string;
  contactReferenceSourceValue?: string;
}

// Also revalidate on read. Exact source matching prevents a later edit,
// redaction or different patient's value from inheriting a formatting receipt.
export function readQuestionnaireChartFormatting(sourceValue: unknown, raw: unknown, lastDoseSourceValue?: unknown, contactReferenceSourceValue?: unknown): QuestionnaireChartFormatting | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Partial<QuestionnaireChartFormatting>;
  if (r.version !== 1 || r.verified !== true || !["medications", "contact"].includes(String(r.kind))
    || JSON.stringify(r.sourceValue) !== JSON.stringify(sourceValue)) return null;
  if (r.lastDoseSourceValue !== undefined && (r.kind !== "medications" || typeof r.lastDoseSourceValue !== "string" || !r.lastDoseSourceValue.trim()
    || r.lastDoseSourceValue.length > 12000 || r.lastDoseSourceValue !== lastDoseSourceValue)) return null;
  if (r.contactReferenceSourceValue !== undefined && (r.kind !== "contact" || typeof r.contactReferenceSourceValue !== "string" || !r.contactReferenceSourceValue.trim()
    || r.contactReferenceSourceValue.length > 12000 || r.contactReferenceSourceValue !== contactReferenceSourceValue)) return null;
  const sources = r.kind === "medications" && Array.isArray(sourceValue) ? sourceValue
    : r.kind === "contact" && typeof sourceValue === "string" ? [sourceValue] : null;
  if (!sources || sources.length < 1 || sources.length > 100 || !sources.every(s => typeof s === "string" && s.length > 0 && s.length <= 12000)
    || !Array.isArray(r.entries) || r.entries.length !== sources.length) return null;
  for (const [i, entry] of r.entries.entries()) {
    if (!entry || typeof entry !== "object" || entry.sourceIndex !== i) return null;
    for (const key of chartFormattingFields) {
      const value = entry[key];
      if (value !== null && (typeof value !== "string" || !value.trim()
        || !(sources[i].includes(value) || (key === "lastTaken" && r.lastDoseSourceValue?.includes(value))
          || (["name", "relationship", "phone"].includes(key) && r.contactReferenceSourceValue?.includes(value))))) return null;
      if (r.kind === "contact" && ["dose", "frequency", "reason", "lastTaken"].includes(key) && value !== null) return null;
      if (r.kind === "medications" && ["relationship", "phone"].includes(key) && value !== null) return null;
    }
  }
  return r as QuestionnaireChartFormatting;
}

// Allergy layout is a distinct protocol: a single captured string may contain
// several members. It grants neither category nor clinical-answer authority.
export type QuestionnaireAllergyScreenContext = Array<{ questionKey: string; promptText: string; sectionKey: string | null; value: boolean }>;
export interface QuestionnaireAllergyFormatting {
  version: 2;
  kind: "allergies";
  sourceValue: string;
  screenContext: QuestionnaireAllergyScreenContext;
  entries: Array<{ name: string; reaction: string | null; screenQuestionKeys: string[] }>;
  verified: true;
}
const exactFormattingKeys = (value: unknown, keys: string[]): value is Record<string, unknown> => !!value && typeof value === "object"
  && !Array.isArray(value) && Object.keys(value).sort().join(",") === [...keys].sort().join(",");
const formattingId = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0 && value.length <= 256
  && !["__proto__", "prototype", "constructor"].includes(value);

/** All accepted authored yes/no context, not a clinical key/name heuristic.
 * The independent model may link only a specific positive SAME-member screen,
 * never a broad category gate. Invalid catalog data cannot create a context. */
export function buildQuestionnaireAllergyScreenContext(input: {
  questions: Array<{ questionKey: string; promptText?: string; answerType?: string; sectionKey?: string | null }>;
  answers: Record<string, { value?: unknown; unresolved?: boolean }>;
}): QuestionnaireAllergyScreenContext | null {
  if (!Array.isArray(input.questions) || input.questions.length > 300 || !input.answers || typeof input.answers !== "object"
      || Array.isArray(input.answers)) return null;
  const keys = new Set<string>(), context: QuestionnaireAllergyScreenContext = [];
  for (const question of input.questions) {
    if (!question || !formattingId(question.questionKey) || keys.has(question.questionKey)) return null;
    keys.add(question.questionKey);
    const answer = input.answers[question.questionKey];
    if (question.answerType !== "yes_no" || !answer || answer.unresolved || typeof answer.value !== "boolean") continue;
    if (typeof question.promptText !== "string" || !question.promptText.trim() || question.promptText.length > 12000
        || (question.sectionKey != null && !formattingId(question.sectionKey))) return null;
    context.push({ questionKey: question.questionKey, promptText: question.promptText, sectionKey: question.sectionKey ?? null, value: answer.value });
  }
  return context;
}

/** Revalidate presentation-only spans and EVERY screen dependency on read.
 * No alias matching, prose splitting, inference or clinical approval occurs. */
export function readQuestionnaireAllergyFormatting(sourceValue: unknown, raw: unknown,
  screenContext: QuestionnaireAllergyScreenContext | null | undefined): QuestionnaireAllergyFormatting | null {
  if (typeof sourceValue !== "string" || !sourceValue.trim() || sourceValue.length > 12000
      || !exactFormattingKeys(raw, ["version", "kind", "sourceValue", "screenContext", "entries", "verified"])
      || raw.version !== 2 || raw.kind !== "allergies" || raw.verified !== true || raw.sourceValue !== sourceValue
      || !Array.isArray(screenContext) || screenContext.length > 300
      || !Array.isArray(raw.screenContext) || raw.screenContext.length !== screenContext.length
      || !Array.isArray(raw.entries) || !raw.entries.length || raw.entries.length > 100) return null;
  const screens = new Map<string, boolean>();
  for (const [index, screen] of screenContext.entries()) {
    const retainedScreen = raw.screenContext[index];
    // JSONB may reorder object keys. Compare every authored dependency by value
    // while retaining exact key sets and the original catalog array order.
    if (!exactFormattingKeys(screen, ["questionKey", "promptText", "sectionKey", "value"])
        || !exactFormattingKeys(retainedScreen, ["questionKey", "promptText", "sectionKey", "value"])
        || !formattingId(screen.questionKey) || screens.has(screen.questionKey) || typeof screen.value !== "boolean"
        || typeof screen.promptText !== "string" || !screen.promptText.trim() || screen.promptText.length > 12000
        || (screen.sectionKey !== null && !formattingId(screen.sectionKey))
        || retainedScreen.questionKey !== screen.questionKey || retainedScreen.promptText !== screen.promptText
        || retainedScreen.sectionKey !== screen.sectionKey || retainedScreen.value !== screen.value) return null;
    screens.set(screen.questionKey, screen.value);
  }
  const linked = new Set<string>(), entries = new Set<string>();
  for (const entry of raw.entries) {
    if (!exactFormattingKeys(entry, ["name", "reaction", "screenQuestionKeys"])
        || typeof entry.name !== "string" || !entry.name.trim() || !sourceValue.includes(entry.name)
        || (entry.reaction !== null && (typeof entry.reaction !== "string" || !entry.reaction.trim() || !sourceValue.includes(entry.reaction)))
        || !Array.isArray(entry.screenQuestionKeys) || entry.screenQuestionKeys.length > screens.size) return null;
    const identity = JSON.stringify([entry.name, entry.reaction]);
    if (entries.has(identity)) return null; entries.add(identity);
    for (const key of entry.screenQuestionKeys) {
      if (typeof key !== "string" || screens.get(key) !== true || linked.has(key)) return null;
      linked.add(key);
    }
  }
  return raw as unknown as QuestionnaireAllergyFormatting;
}
